import React, { useContext, useEffect, useState } from 'react';
import { ThemeContext } from '../../Infrastructure/Context/ThemeContext';
import { userIsInRole, ADMIN, CLIENTADMIN } from '../../Services/AuthService';
import './Navbar.scss';
import { Fade as Hamburger } from 'hamburger-react';
import { blackOrWhite } from '../../Infrastructure/ManipulateColors';
import LineIcon from 'react-lineicons';
import { NavLink } from 'react-router-dom';
import CosmoAvatar from '../../Assets/Cosmo.png';
import { askAiAssistantAsync } from '../../Services/ReportService';
import { clientListAsync } from '../../Services/AdminService';
import { useAuthState } from '../../Services/AuthService';

const Navbar = () => {
    const context = useContext(ThemeContext);
    const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 992);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [cosmoOpen, setCosmoOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [showWelcome, setShowWelcome] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState('');
    const auth = useAuthState();
    const user = auth.user || {};

    const isAdmin = userIsInRole(ADMIN);
    const isClientAdmin = userIsInRole(CLIENTADMIN);
    const canUseAiAssistant = isAdmin || isClientAdmin;

    useEffect(() => {
        if (user?.clientId && !isAdmin) {
            setSelectedClientId(user.clientId);
        }

        // Check if user has seen Cosmo before
        if (canUseAiAssistant) {
            const hasSeenCosmo = localStorage.getItem('hasSeenCosmo');
            if (!hasSeenCosmo) {
                setTimeout(() => setShowWelcome(true), 1000);
            }
        }
    }, [isAdmin, canUseAiAssistant, user]);

    useEffect(() => {
        const fetchClients = async () => {
            if (!isAdmin) {
                return;
            }

            try {
                const response = await clientListAsync();
                setClients(response?.clients || []);
            } catch (error) {
                console.error('Error fetching clients for AI assistant:', error);
            }
        };

        fetchClients();
    }, [isAdmin]);

    useEffect(() => {
        let open = sidebarOpen;
        document.getElementById('sidebar')
            .classList.remove(!open ? 'sidebar-open' : 'sidebar-hidden');
        document.getElementById('sidebar')
            .classList.add(!open ? 'sidebar-hidden' : 'sidebar-open');

        !open ? document.getElementById('main-container').classList.remove('menu-toggle')
            : document.getElementById('main-container').classList.add('menu-toggle')

        !open ? document.getElementById('admin-navbar').classList.remove('menu-toggle')
            : document.getElementById('admin-navbar').classList.add('menu-toggle')
    }, [sidebarOpen])

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    }

    const getInitials = () => {
        if (user.firstName && user.lastName) {
            return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
        }
        return "";
    }

    const handleSendMessage = async () => {
        if (inputMessage.trim() && !isLoading) {
            const userMessage = inputMessage;
            setMessages(prev => [...prev, { text: userMessage, sender: 'user' }]);
            setInputMessage('');
            setIsLoading(true);

            try {
                const clientId = isAdmin ? (selectedClientId || null) : (user?.clientId || selectedClientId || null);
                const selectedClient = clients.find((client) => String(client.id) === String(selectedClientId));
                const scopeLabel = isAdmin
                    ? (selectedClientId ? (selectedClient?.name || 'Selected Client') : 'All Clients')
                    : 'My Client';

                const response = await askAiAssistantAsync(userMessage, clientId);
                setMessages(prev => [...prev, { text: response.answer, sender: 'cosmo', scopeLabel }]);
            } catch (error) {
                console.error('Error calling AI Assistant:', error);
                setMessages(prev => [...prev, {
                    text: 'Sorry, I encountered an error processing your request. Please try again.',
                    sender: 'cosmo'
                }]);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleSuggestedPrompt = (prompt) => {
        setInputMessage(prompt);
    };

    const handleDismissWelcome = () => {
        setShowWelcome(false);
        localStorage.setItem('hasSeenCosmo', 'true');
    };

    const handleOpenCosmo = () => {
        setCosmoOpen(!cosmoOpen);
        if (showWelcome) {
            handleDismissWelcome();
        }
    };

    const suggestedPrompts = [
        'What is the average completion rate for all courses?',
        'Which courses have the highest engagement?',
        'How many learners completed courses this month?',
        'Show me learner activity trends'
    ];

    return (
        <div id="admin-navbar" className="admin-navbar menu-toggle">
            <div className="navbar-content" style={{ "background": context.secondaryNavColour }}>
                <div className="navbar-left">
                    <Hamburger rounded toggled={sidebarOpen} toggle={() => toggleSidebar()} color={context.secondaryNavColour ? blackOrWhite(context.secondaryNavColour) : "black"} />
                </div>

                <div className="navbar-right">
                    {canUseAiAssistant && (
                        <div className="cosmo-button-wrapper" style={{ marginRight: '15px', position: 'relative' }}>
                            {showWelcome && (
                                <div style={{
                                    position: 'absolute',
                                    top: '50px',
                                    right: '0',
                                    width: '280px',
                                    background: 'white',
                                    borderRadius: '12px',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                    padding: '16px',
                                    zIndex: 10001,
                                    animation: 'slideDown 0.3s ease-out'
                                }}>
                                    <style>{
                                        `@keyframes slideDown {
                                            from {
                                                opacity: 0;
                                                transform: translateY(-10px);
                                            }
                                            to {
                                                opacity: 1;
                                                transform: translateY(0);
                                            }
                                        }`
                                    }</style>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                        <img src={CosmoAvatar} alt="Cosmo" style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0 }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: '700', fontSize: '1rem', color: '#333', marginBottom: '6px' }}>
                                                👋 Hi! I'm Cosmo
                                            </div>
                                            <div style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.4', marginBottom: '12px' }}>
                                                Your AI reporting assistant. Click my icon anytime to ask questions about your data!
                                            </div>
                                            <button
                                                onClick={handleDismissWelcome}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px',
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    fontSize: '0.9rem',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    transition: 'opacity 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                                                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                            >
                                                Got it!
                                            </button>
                                        </div>
                                        <button
                                            onClick={handleDismissWelcome}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                color: '#999',
                                                cursor: 'pointer',
                                                fontSize: '1.2rem',
                                                padding: '0',
                                                width: '20px',
                                                height: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            )}
                            <button
                                onClick={handleOpenCosmo}
                                style={{
                                    background: cosmoOpen ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                                    border: `2px solid ${cosmoOpen ? '#667eea' : 'transparent'}`,
                                    borderRadius: '50%',
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    width: '44px',
                                    height: '44px'
                                }}
                                onMouseEnter={(e) => {
                                    if (!cosmoOpen) {
                                        e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
                                        e.currentTarget.style.borderColor = '#667eea';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!cosmoOpen) {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.borderColor = 'transparent';
                                    }
                                }}
                                title="Ask Cosmo - AI Assistant"
                            >
                                <img src={CosmoAvatar} alt="Cosmo" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
                            </button>
                        </div>
                    )}
                    <div className="user-dropdown-wrapper">
                        <div className="user-profile" onClick={() => setDropdownOpen(!dropdownOpen)}>
                            <div className="profile-initials" style={{ backgroundColor: context.primaryNavColour || '#079fd0', color: context.iconColour || '#fff' }}>
                                {getInitials()}
                            </div>
                            <div className="user-info" style={{ color: context.secondaryNavColour ? blackOrWhite(context.secondaryNavColour) : "black" }}>
                                <span className="user-name">{user.firstName} {user.lastName}</span>
                                <LineIcon name="chevron-down" className={`chevron ${dropdownOpen ? 'open' : ''}`} />
                            </div>
                        </div>

                        {dropdownOpen && (
                            <div className="user-dropdown-menu">
                                <NavLink to="/my-account" className="dropdown-item" onClick={() => setDropdownOpen(false)}>
                                    <LineIcon name="user" />
                                    <span>My Account</span>
                                </NavLink>
                                <div className="dropdown-divider"></div>
                                <NavLink to="/" className="dropdown-item logout" data-toggle="modal" data-target="#logoutModal" onClick={() => setDropdownOpen(false)}>
                                    <LineIcon name="exit" />
                                    <span>Logout</span>
                                </NavLink>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {canUseAiAssistant && cosmoOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        onClick={() => setCosmoOpen(false)}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0,0,0,0.3)',
                            zIndex: 9999,
                            backdropFilter: 'blur(2px)'
                        }}
                    />

                    {/* Chat Window */}
                    <div style={{
                        position: 'fixed',
                        top: '80px',
                        right: '20px',
                        width: '400px',
                        maxWidth: 'calc(100vw - 40px)',
                        height: 'calc(100vh - 120px)',
                        maxHeight: '600px',
                        background: 'white',
                        borderRadius: '16px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                        display: 'flex',
                        flexDirection: 'column',
                        zIndex: 10000,
                        overflow: 'hidden'
                    }}>
                        {/* Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            padding: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            color: 'white',
                            flexShrink: 0
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <img src={CosmoAvatar} alt="Cosmo" style={{ width: '40px', height: '40px', borderRadius: '50%', border: '2px solid white' }} />
                                <div>
                                    <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>Cosmo</div>
                                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>AI Reporting Assistant</div>
                                </div>
                            </div>
                            <button
                                onClick={() => setCosmoOpen(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '1.5rem',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {/* Messages */}
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '20px',
                            background: '#f8f9fa',
                            minHeight: 0
                        }}>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    color: '#666',
                                    display: 'block',
                                    marginBottom: '6px'
                                }}>
                                    Ask about
                                </label>
                                <select
                                    value={selectedClientId}
                                    onChange={(e) => setSelectedClientId(e.target.value)}
                                    disabled={isLoading || !isAdmin}
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        background: (isLoading || !isAdmin) ? '#f5f5f5' : 'white'
                                    }}
                                >
                                    {isAdmin && <option value="">All Clients</option>}
                                    {isAdmin
                                        ? clients.map((client) => (
                                            <option key={client.id} value={client.id}>{client.name}</option>
                                        ))
                                        : <option value={user?.clientId || selectedClientId || ''}>My Client</option>
                                    }
                                </select>
                            </div>

                            {messages.length === 0 ? (
                                <div>
                                    <div style={{ textAlign: 'center', color: '#666', marginBottom: '20px' }}>
                                        <div style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '10px' }}>👋 Hi! I'm Cosmo</div>
                                        <div style={{ fontSize: '0.9rem' }}>Ask me anything about your reporting data!</div>
                                    </div>
                                    <div style={{ marginTop: '20px' }}>
                                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#555', marginBottom: '12px' }}>Suggested prompts:</div>
                                        {suggestedPrompts.map((prompt, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => handleSuggestedPrompt(prompt)}
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    textAlign: 'left',
                                                    padding: '12px 16px',
                                                    marginBottom: '8px',
                                                    background: 'white',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    fontSize: '0.9rem',
                                                    color: '#333',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.background = '#f0f0f0';
                                                    e.currentTarget.style.borderColor = '#667eea';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = 'white';
                                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                                }}
                                            >
                                                💡 {prompt}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                messages.map((msg, idx) => (
                                    <div key={idx} style={{
                                        marginBottom: '12px',
                                        display: 'flex',
                                        justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start'
                                    }}>
                                        {msg.sender === 'cosmo' && (
                                            <img src={CosmoAvatar} alt="Cosmo" style={{ width: '30px', height: '30px', borderRadius: '50%', marginRight: '8px' }} />
                                        )}
                                        <div style={{
                                            padding: '12px 16px',
                                            borderRadius: '12px',
                                            maxWidth: '75%',
                                            background: msg.sender === 'user' ? '#667eea' : 'white',
                                            color: msg.sender === 'user' ? 'white' : '#333',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                            fontSize: '0.9rem',
                                            whiteSpace: 'pre-wrap'
                                        }}>
                                            {msg.sender === 'cosmo' && msg.scopeLabel && (
                                                <div style={{
                                                    display: 'inline-block',
                                                    marginBottom: '8px',
                                                    padding: '2px 8px',
                                                    borderRadius: '999px',
                                                    background: '#eef2ff',
                                                    color: '#4f46e5',
                                                    fontSize: '0.72rem',
                                                    fontWeight: '600'
                                                }}>
                                                    Scope: {msg.scopeLabel}
                                                </div>
                                            )}
                                            {msg.text}
                                        </div>
                                    </div>
                                ))
                            )}
                            {isLoading && (
                                <div style={{
                                    marginBottom: '12px',
                                    display: 'flex',
                                    justifyContent: 'flex-start'
                                }}>
                                    <img src={CosmoAvatar} alt="Cosmo" style={{ width: '30px', height: '30px', borderRadius: '50%', marginRight: '8px' }} />
                                    <div style={{
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        background: 'white',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        fontSize: '0.9rem'
                                    }}>
                                        <span style={{ animation: 'pulse 1.5s infinite' }}>Thinking...</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Input */}
                        <div style={{
                            padding: '16px',
                            background: 'white',
                            borderTop: '1px solid #e0e0e0',
                            flexShrink: 0
                        }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <input
                                    type="text"
                                    value={inputMessage}
                                    onChange={(e) => setInputMessage(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleSendMessage()}
                                    placeholder={isLoading ? "Cosmo is thinking..." : "Ask about your data..."}
                                    disabled={isLoading}
                                    style={{
                                        flex: 1,
                                        padding: '12px 16px',
                                        border: '2px solid #e0e0e0',
                                        borderRadius: '8px',
                                        fontSize: '0.95rem',
                                        outline: 'none',
                                        transition: 'border-color 0.2s ease',
                                        opacity: isLoading ? 0.6 : 1
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                />
                                <button
                                    onClick={handleSendMessage}
                                    disabled={!inputMessage.trim() || isLoading}
                                    style={{
                                        padding: '12px 20px',
                                        background: (inputMessage.trim() && !isLoading) ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#ccc',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: 'white',
                                        cursor: (inputMessage.trim() && !isLoading) ? 'pointer' : 'not-allowed',
                                        fontWeight: '600',
                                        fontSize: '0.95rem',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {isLoading ? '...' : 'Send'}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Navbar;